import { useEffect, useState, useMemo } from 'react';
import { History, PhoneOff } from 'lucide-react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import type { LiveCallResponse } from '@vagusx/clients';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useLiveCalls } from '@/hooks/use-live-calls';
import { useSecondTicker } from '@/hooks/use-second-ticker';
import { liveCountToShow } from '@/lib/live-count';
import { CountChip } from '../CountChip';
import { LiveCallRow } from './LiveCallRow';
import { LiveCallRail } from './LiveCallRail';
import { LiveCallTranscript } from './LiveCallTranscript';
import {
  callerInitials,
  callerName,
  directionLabel,
  displayedElapsed,
  formatElapsed,
  reasonLabel,
  toneStyle,
} from './format';

/**
 * Live Calls — the roster on the left, the selected call's transcript in
 * the middle, what we know about it on the right.
 *
 * TWO CLOCKS, DELIBERATELY. The roster polls every five seconds
 * (`useLiveCalls`); the transcript of whichever call is selected streams
 * over the existing per-interaction SSE endpoint. A call's position in the
 * list changes on the order of tens of seconds, but the words do not, and
 * subscribing to every call at once would open one stream per row.
 */
export default function LiveCallsPage(): React.ReactElement {
  // A link can name the call to open (`?call=`): Listen on a campaign's queue
  // (ADR-318, H). It is the first choice; a click replaces it.
  const { call: linkedCallId } = useSearch({ from: '/_authenticated/calls/live' });
  const { data, isLoading, isError, dataUpdatedAt } = useLiveCalls();
  const calls = useMemo(() => data?.items ?? [], [data]);

  // The user's EXPLICIT choice, or null for "haven't picked one".
  //
  // Nothing writes the fallback back into this. An effect that did would
  // pin whichever call happened to be first when the page loaded, and the
  // follow-the-top behaviour below would then only ever happen once — so
  // an escalated call arriving at the top of a worst-first roster would
  // never be surfaced to someone who hasn't clicked anything.
  const [selectedId, setSelectedId] = useState<string | null>(linkedCallId ?? null);
  // A new `?call=` on the same page (another Listen link) is a new choice.
  useEffect(() => {
    if (linkedCallId) setSelectedId(linkedCallId);
  }, [linkedCallId]);
  // A click is a choice too, and the URL follows it, so a reload or a shared
  // link opens the call being watched, not the one the page was opened on.
  const navigate = useNavigate();
  const choose = (id: string): void => {
    setSelectedId(id);
    void navigate({ to: '/calls/live', search: { call: id }, replace: true });
  };

  // Follow the list rather than fighting it: with nothing chosen, or when
  // the chosen call drops off the roster because it ended, land on
  // whatever is now at the top. The roster is sorted worst-first, so that
  // is the call most likely to want a human. A click pins it until that
  // call ends.
  const selected: LiveCallResponse | null =
    calls.find((call) => call.id === selectedId) ?? calls[0] ?? null;

  // No calls, no clocks — don't re-render the page once a second to count
  // time on an empty list.
  const now = useSecondTicker(calls.length > 0);

  return (
    <div className="flex h-full min-h-0">
      <aside className="flex w-[306px] flex-shrink-0 flex-col border-r">
        <header className="flex items-center gap-2 border-b px-3.5 py-3.5">
          <h1 className="text-base font-semibold">Live Calls</h1>
          <CountChip count={liveCountToShow({ data, isError })} />
          <span className="flex-grow" />
          <span className="inline-flex items-center gap-1.5 text-[11.5px] text-muted-foreground">
            <span
              className={cn(
                'h-1.5 w-1.5 rounded-full',
                isError ? 'bg-destructive' : 'animate-pulse bg-success'
              )}
            />
            {isError ? 'not updating' : 'streaming'}
          </span>
        </header>

        <div className="border-b px-3.5 py-2">
          <Button variant="outline" size="sm" className="w-full" asChild>
            <Link to="/calls">
              <History className="h-3.5 w-3.5" />
              Calls
            </Link>
          </Button>
        </div>

        <div className="flex-grow overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col gap-3 p-3.5">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          ) : null}

          {!isLoading && calls.length === 0 ? (
            <p className="px-4 py-10 text-center text-xs text-muted-foreground">
              {isError ? "Couldn't load live calls." : 'No calls on the line right now.'}
            </p>
          ) : null}

          {calls.map((call) => (
            <LiveCallRow
              key={call.id}
              call={call}
              isSelected={call.id === selected?.id}
              fetchedAt={dataUpdatedAt}
              now={now}
              onSelect={() => choose(call.id)}
            />
          ))}
        </div>
      </aside>

      {selected ? (
        <>
          <main className="flex min-w-0 flex-grow flex-col">
            <CallHeader call={selected} fetchedAt={dataUpdatedAt} now={now} />
            <div className="min-h-0 flex-grow">
              {/*
                Keyed by id so switching calls remounts the transcript.
                Without it the SSE hook would carry the previous call's
                merged message list into the new subscription for a frame.
              */}
              <LiveCallTranscript key={selected.id} interactionId={selected.id} isLive />
            </div>
          </main>
          <LiveCallRail call={selected} onListenStart={choose} />
        </>
      ) : (
        <main className="flex flex-grow flex-col items-center justify-center gap-3 text-muted-foreground">
          {/*
            "Nothing on the line" and "we couldn't ask" are different
            claims, and only one of them is reassuring. A failed request
            must never render as a quiet, confident empty state on a page
            someone is watching to know whether the phones are working.
          */}
          <PhoneOff className="h-7 w-7" />
          <p className="text-sm">
            {isError ? "Couldn't load live calls." : 'Nothing on the line.'}
          </p>
          {isError ? (
            <p className="max-w-xs text-center text-xs">
              The roster didn't load, so this is not a statement about whether any calls are
              happening.
            </p>
          ) : null}
        </main>
      )}
    </div>
  );
}

function CallHeader({
  call,
  fetchedAt,
  now,
}: {
  call: LiveCallResponse;
  fetchedAt: number;
  now: number;
}): React.ReactElement {
  const reason = reasonLabel(call.reason);
  const tone = toneStyle(call.tone);
  const elapsed = displayedElapsed(call.elapsedSeconds, fetchedAt, now);

  return (
    <header className="flex items-center gap-3.5 border-b px-5 py-3.5">
      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-[13px] font-semibold text-primary">
        {callerInitials(call)}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h2 className="truncate text-base font-semibold">{callerName(call)}</h2>
          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
            {directionLabel(call.direction)}
            {reason ? ` · ${reason}` : ''}
          </span>
        </div>
        <p className="mt-0.5 font-mono text-xs text-muted-foreground">
          {call.phoneNumber ?? 'No number'}
          {call.agentName ? ` · ${call.agentName}` : ''}
        </p>
      </div>
      <span className="flex-grow" />
      <div className="text-right">
        <div className={cn('font-mono text-xl font-semibold tabular-nums', tone.text)}>
          {formatElapsed(elapsed)}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {call.connected ? 'on the call' : 'ringing'}
        </div>
      </div>
    </header>
  );
}
